/**
 * Checkbox Component
 *
 * Accessible checkbox with dark theme styling and indeterminate state support.
 * Supports keyboard navigation (Space/Enter) and screen readers.
 */

import React, { useRef, useEffect, useCallback } from 'react';
import { Square, CheckSquare, MinusSquare } from 'lucide-react';

export interface CheckboxProps {
  /** Whether checkbox is checked */
  checked: boolean;
  /** Change handler */
  onChange: (checked: boolean) => void;
  /** Indeterminate state (partial selection) */
  indeterminate?: boolean;
  /** Icon size in pixels */
  size?: 12 | 16 | 20;
  /** Additional CSS classes */
  className?: string;
  /** Disabled state */
  disabled?: boolean;
  /** Accessible label for screen readers */
  'aria-label'?: string;
  /** ID of element that labels this checkbox */
  'aria-labelledby'?: string;
}

/**
 * Checkbox component with three states: unchecked, checked, indeterminate
 * Fully accessible with keyboard support and ARIA attributes.
 *
 * @example
 * ```tsx
 * <Checkbox
 *   checked={isChecked}
 *   onChange={(checked) => setIsChecked(checked)}
 *   indeterminate={someChecked && !allChecked}
 *   size={12}
 *   aria-label="Select all items"
 * />
 * ```
 */
export const Checkbox: React.FC<CheckboxProps> = ({
  checked,
  onChange,
  indeterminate = false,
  size = 16,
  className = '',
  disabled = false,
  'aria-label': ariaLabel,
  'aria-labelledby': ariaLabelledBy,
}) => {
  const buttonRef = useRef<HTMLButtonElement>(null);

  const handleToggle = useCallback(() => {
    if (!disabled) {
      onChange(!checked);
    }
  }, [disabled, checked, onChange]);

  const handleKeyDown = useCallback((e: React.KeyboardEvent) => {
    if (e.key === ' ' || e.key === 'Enter') {
      e.preventDefault();
      handleToggle();
    }
  }, [handleToggle]);

  const iconClassName = `transition-all duration-200 ${
    disabled
      ? 'text-dark-muted opacity-50'
      : checked
      ? 'text-brand-400 drop-shadow-[0_0_3px_rgba(var(--brand-rgb,99,102,241),0.3)]'
      : 'text-dark-muted hover:text-dark-text'
  }`;

  const Icon = indeterminate ? MinusSquare : checked ? CheckSquare : Square;

  return (
    <button
      ref={buttonRef}
      type="button"
      role="checkbox"
      aria-checked={indeterminate ? 'mixed' : checked}
      aria-label={ariaLabel}
      aria-labelledby={ariaLabelledBy}
      aria-disabled={disabled}
      disabled={disabled}
      onClick={handleToggle}
      onKeyDown={handleKeyDown}
      className={`
        inline-flex items-center justify-center
        rounded-md transition-all duration-200 ease-out
        focus:outline-none focus:ring-2 focus:ring-brand-400/50 focus:ring-offset-1 focus:ring-offset-dark-bg
        ${disabled ? 'cursor-not-allowed' : 'cursor-pointer hover:bg-dark-surface/60'}
        ${className}
      `}
      style={{ padding: 2 }}
      data-interaction-id='button-toggle'>
      <Icon size={size} className={iconClassName} />
    </button>
  );
};
